import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { User, Crown, Calendar, Trash2, Loader2, AlertCircle, RefreshCw, Edit2, X, Save, Eye, EyeOff } from 'lucide-react';

function UsersList({ userRole }) {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ username: '', password: '' });
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_URL + '/api/users', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) throw new Error('Kullanıcılar alınamadı');
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinize emin misiniz?')) return;
    try {
      const response = await fetch(API_URL + `/api/users?id=${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) throw new Error('Kullanıcı silinemedi');
      setUsers(users.filter(user => user.id !== userId));
    } catch (error) {
      alert('Kullanıcı silinirken bir hata oluştu.');
    }
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    setEditForm({ username: user.username, password: '' });
    setEditError('');
    setShowPassword(false);
  };

  const handleEditCancel = () => {
    setEditingUser(null);
    setEditForm({ username: '', password: '' });
    setEditError('');
  };

  const handleEditSave = async () => {
    if (!editForm.username.trim()) {
      setEditError('Kullanıcı adı gerekli.');
      return;
    }
    if (editForm.password && editForm.password.length < 6) {
      setEditError('Şifre en az 6 karakter olmalı.');
      return;
    }

    setEditLoading(true);
    setEditError('');

    try {
      const updateData = { username: editForm.username.trim() };
      if (editForm.password) {
        updateData.password = editForm.password;
      }

      const response = await fetch(API_URL + `/api/users?id=${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Güncelleme başarısız');
      }

      const updatedUser = await response.json();
      setUsers(users.map(u => u.id === updatedUser.id ? updatedUser : u));
      setEditingUser(null);
      setEditForm({ username: '', password: '' });
    } catch (error) {
      setEditError(error.message);
    } finally {
      setEditLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-8 text-center">
        <Loader2 className="w-8 h-8 text-violet-500 animate-spin mx-auto mb-3" />
        <p className="text-zinc-500 text-sm">Yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-zinc-900 border border-red-500/30 rounded-lg p-8 text-center">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
        <p className="text-red-400 text-sm mb-3">{error}</p>
        <button onClick={fetchUsers} className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm rounded-lg transition-colors mx-auto">
          <RefreshCw className="w-4 h-4" /> Tekrar Dene
        </button>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-8 text-center">
        <User className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
        <p className="text-zinc-500 text-sm">Henüz kullanıcı yok</p>
      </div>
    );
  }

  return (
    <>
      {/* Düzenleme Modalı */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Yönetici Düzenle</h3>
              <button onClick={handleEditCancel} className="p-1 text-zinc-500 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-zinc-500 mb-1.5">Kullanıcı Adı</label>
                <input
                  type="text"
                  value={editForm.username}
                  onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500 transition-colors"
                  placeholder="Kullanıcı adı"
                />
              </div>

              <div>
                <label className="block text-xs text-zinc-500 mb-1.5">Yeni Şifre <span className="text-zinc-600">(değiştirmek için doldurun)</span></label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={editForm.password}
                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                    className="w-full px-3 py-2 pr-10 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500 transition-colors"
                    placeholder="Yeni şifre (opsiyonel)"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-500 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {editError && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {editError}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleEditSave}
                  disabled={editLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
                >
                  {editLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Kaydet
                </button>
                <button
                  onClick={handleEditCancel}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm rounded-lg transition-colors"
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {users.map((user) => (
          <div key={user.id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 hover:border-zinc-700 transition-colors">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 ${user.role === 'super_user' ? 'bg-amber-600/20 text-amber-400' : 'bg-violet-600/20 text-violet-400'}`}>
                  {user.role === 'super_user' ? <Crown className="w-5 h-5" /> : user.username?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{user.username}</p>
                  <span className={`text-xs ${user.role === 'super_user' ? 'text-amber-400' : 'text-zinc-500'}`}>
                    {user.role === 'super_user' ? 'Süper Yönetici' : 'Yönetici'}
                  </span>
                </div>
              </div>
              {user.role !== 'super_user' && userRole === 'super_user' && (
                <div className="flex items-center gap-1">
                  <button onClick={() => handleEditClick(user)} className="p-2 text-zinc-500 hover:text-violet-400 hover:bg-violet-500/10 rounded-lg transition-colors" title="Düzenle">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(user.id)} className="p-2 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" title="Sil">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center gap-1 text-xs text-zinc-500">
              <Calendar className="w-3 h-3" />
              {user.createdAt ? new Date(user.createdAt).toLocaleDateString('tr-TR') : '-'}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export default UsersList;
